import uniqid from 'uniqid';
import ReactiveComponent, { watch } from '@utils/reactive-component';
import FocusTrap from '@utils/focus-trap';
import keyCodes from '@utils/key-codes';
import parseRenderingContext from '@utils/parse-rendering-context';
import { countEl } from './templates/mini-cart-toggle';
import { context as defaultContext } from './mini-cart-defaults';
import MiniCart from './mini-cart';
import cartStore from '@stores/cart/cart-store';
import * as miniCartDatalayer from '@datalayer/components/mini-cart-datalayer';

const $document = $(document);

const CLASS_NAME_COUNT_VISIBLE = '-visible';

const EVENT_KEY = '_miniCartToggle_';
const EVENT_CLICK = `click.${EVENT_KEY}`;
const EVENT_KEYUP_ESCAPE = `keyup.${EVENT_KEY}`;

const DEFAULT_FOCUS_TRAP_OPTIONS = {
    allowBreakout: true,
    nextKeys: [ keyCodes.ARROW_DOWN ],
    prevKeys: [ keyCodes.ARROW_UP ]
};

export default class MiniCartToggle extends ReactiveComponent {
    #focusTrap = null;

    constructor($el, context) {
        super();

        this.context = { ...defaultContext, ...context };

        this.$el = $el;
        this.$count = $(countEl);
        this.$menu = this.$el.find('[data-region="menu"]');
        this.$toggleCtrl = this.$el.find('[data-control="toggle"]');
        this.$miniCartPlaceholder = this.$el.find('[data-placeholder="miniCart"]');

        this.id = uniqid.time('miniCartToggle_');
        this.miniCart = null;

        this.initialFocus = true;

        this.reactive({
            visible: false
        });
    }

    [watch]() {
        return {
            visible() {
                this.visible ? this.showMenu() : this.hideMenu();
                this.$toggleCtrl.attr('aria-expanded', this.visible);
            }
        }
    }

    init() {
        this.renderCount();
        this.setupAccessibility();
        this.setupMenu();
        this.setupListeners();
    }

    onKeyupEsc(key) {
        if(key === keyCodes.ESCAPE) {
            this.hide();
        }
    }

    onClickOut(evt) {
        const $target = $(evt.target);
        if(!$target.closest(this.$el).length) {
            this.hide();
        }
    }

    onToggleClick() {
        this.toggle();

        if(this.visible) {
            miniCartDatalayer.sendImpression();
        }
    }

    onToggleKeyup(key) {
        const keyIndex = [ keyCodes.ARROW_UP, keyCodes.ARROW_DOWN ].indexOf(key);
        if(keyIndex > -1 && !this.visible) {
            this.show();
            miniCartDatalayer.sendImpression();

            if(keyIndex) {
                if(this.#focusTrap?.$current.is(this.$toggleCtrl)) {
                    this.#focusTrap.next();
                }
            } else {
                this.#focusTrap?.last();
            }
        }
    }

    renderCount() {
        this.$toggleCtrl.append(this.$count);
    }

    setFocusTrap() {
        this.#focusTrap = new FocusTrap(this.$el, {
            ...DEFAULT_FOCUS_TRAP_OPTIONS,
            initialFocus: this.initialFocus,
            onBreakout: () => this.hide()
        });
    }

    setupAccessibility() {
        this.$toggleCtrl.attr({ 'id': this.id, 'aria-expanded': false });
    }

    setupListeners() {
        this.$toggleCtrl.on('click', () => this.onToggleClick());
        this.$toggleCtrl.on('keyup', (evt) => this.onToggleKeyup(evt.which || evt.keyCode));

        cartStore.watch('lines', () => this.updateCount());
    }

    setupMenu() {
        this.miniCart = new MiniCart(this.$miniCartPlaceholder, this.context, {
            ariaLabelElementId: this.id,
            replaceOriginEl: true
        });

        this.miniCart.init();
        this.miniCart.render();
    }

    updateCount() {
        if(cartStore.getters.lines.length) {
            this.$count.addClass(CLASS_NAME_COUNT_VISIBLE).html(cartStore.getters.linesCount);
        } else {
            this.$count.removeClass(CLASS_NAME_COUNT_VISIBLE);
        }
    }

    focus(productInfo) {
        this.initialFocus = false;

        this.show();
        this.miniCart.focus(productInfo);

        this.initialFocus = true;
    }

    show() {
        this.visible = true
    }

    hide() {
        this.visible = false;
    }

    toggle() {
        this.visible = !this.visible;
    }

    showMenu() {
        this.$menu.fadeIn('fast');
        this.setFocusTrap();
        this.#focusTrap?.$focusable.on(EVENT_KEYUP_ESCAPE, (evt) => this.onKeyupEsc(evt.which || evt.keyCode));

        $document.on(EVENT_CLICK, (evt) => this.onClickOut(evt));
    }

    hideMenu() {
        this.$menu.fadeOut('fast');

        this.#focusTrap?.$focusable.off(EVENT_KEYUP_ESCAPE);
        this.#focusTrap = this.#focusTrap?.destroy() || null;

        $document.off(EVENT_CLICK);
    }
}

let miniCartToggle = null;

$(() => {
    const el = document.querySelector('.js-mini-cart-toggle');
    if(el) {
        const $el = $(el);
        const renderingContext = parseRenderingContext(el);

        miniCartToggle = new MiniCartToggle($el, renderingContext);
        miniCartToggle.init();
    }
});

export {
    miniCartToggle
};
